import React, { useContext, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';

import quizInstanceAdminStatusContext from '../context/quizInstanceAdminStatusContext';
import QuizAdminQuestionList from './QuizAdminQuestionList';

const MEDALS = [
  { color: '#B8860B', bg: '#fffbe6', label: '🥇' },
  { color: '#808080', bg: '#f5f5f5', label: '🥈' },
  { color: '#8B4513', bg: '#fdf5ec', label: '🥉' },
];

// Font sizes per rank (0-indexed). Index 0 = 1st place.
const RANK_FONT   = [28, 22, 18, 14, 14];
const NAME_FONT   = [22, 18, 15, 13, 13];
const POINTS_FONT = [22, 18, 15, 13, 13];
const PADDING     = ['14px 18px', '11px 15px', '9px 13px', '7px 12px', '7px 12px'];

function getRankFont(i)   { return RANK_FONT[Math.min(i, RANK_FONT.length - 1)]; }
function getNameFont(i)   { return NAME_FONT[Math.min(i, NAME_FONT.length - 1)]; }
function getPointsFont(i) { return POINTS_FONT[Math.min(i, POINTS_FONT.length - 1)]; }
function getPadding(i)    { return PADDING[Math.min(i, PADDING.length - 1)]; }

export default function QuizAdminLeaderboard() {

  const ctx = useContext(quizInstanceAdminStatusContext);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const leaderboard = ctx.quizInfo.leaderboard || [];
  const totalCount = ctx.quizInfo.participantTotalCount || 0;
  const answeredCount = ctx.quizInfo.participantAnsweredCount || 0;
  const maxPoints = leaderboard.length > 0 ? leaderboard[0].points : 1;

  // Blur the last 3 entries, but never podium places (top 3)
  const blurThreshold = Math.max(3, leaderboard.length - 3);

  return (
    <Container fluid id="quizAdminStatsWrapper" className="h-100">
      <Row noGutters className="h-100">

        {/* ── Left column: correct answers ── */}
        <Col id="quizAdminStatsLeft" lg={3} className="h-100">
          <QuizAdminQuestionList
            quizInfo={ctx.quizInfo}
            quizStyle={ctx.quizStyle}
            selectedQuestionId=""
          />
        </Col>

        {/* ── Right column: leaderboard ── */}
        <Col className="pl-4 pt-3" style={{ overflowY: 'auto' }}>

          {leaderboard.length === 0 && (
            <div style={{ textAlign: 'center', color: '#aaa', marginTop: '48px', fontSize: '15px' }}>
              Väntar på svar...
            </div>
          )}

          {ctx.quizInfo.completed && leaderboard.length > 0 && (
            <div className="mb-4">
              <h1 className="font-weight-bold">Grattis {leaderboard[0].alias}!</h1>
              <h2>Du knep guldet 🏆</h2>
            </div>
          )}

          <div style={{ maxWidth: '520px' }}>
            {leaderboard.map((entry, index) => {
              const medal = MEDALS[index];
              const rankColor = medal ? medal.color : '#aaa';
              const cardBg   = medal ? medal.bg    : '#fff';
              const fraction = maxPoints > 0 ? entry.points / maxPoints : 0;

              return (
                <div
                  key={entry.alias}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: index < 3 ? '10px' : '6px',
                    padding: getPadding(index),
                    borderRadius: '10px',
                    background: cardBg,
                    boxShadow: index < 3
                      ? `0 2px 8px rgba(0,0,0,0.13)`
                      : '0 1px 3px rgba(0,0,0,0.07)',
                    gap: '12px',
                    transition: 'filter 0.3s ease',
                    filter: index >= blurThreshold && hoveredIndex !== index ? 'blur(4px)' : 'none',
                  }}>

                  {/* Rank number */}
                  <div style={{
                    fontWeight: 'bold',
                    fontSize: `${getRankFont(index)}px`,
                    color: rankColor,
                    minWidth: '32px',
                    textAlign: 'center',
                    lineHeight: 1,
                  }}>
                    {index + 1}
                  </div>

                  {/* Name + progress bar */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontWeight: index < 3 ? 700 : 500,
                      fontSize: `${getNameFont(index)}px`,
                      marginBottom: '5px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      color: index < 3 ? rankColor : '#333',
                    }}>
                      {entry.alias}
                    </div>
                    <div style={{ height: index < 3 ? '8px' : '5px', background: '#ececec', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{
                        height: '100%',
                        width: `${Math.round(fraction * 100)}%`,
                        background: rankColor,
                        borderRadius: '3px',
                        transition: 'width 0.5s ease',
                      }} />
                    </div>
                  </div>

                  {/* Points */}
                  <div style={{
                    fontWeight: 700,
                    fontSize: `${getPointsFont(index)}px`,
                    color: index < 3 ? rankColor : '#555',
                    minWidth: '52px',
                    textAlign: 'right',
                  }}>
                    {Number(entry.points % 1 === 0 ? entry.points : entry.points.toFixed(1))} p
                  </div>

                </div>
              );
            })}
          </div>

          <div style={{ color: '#999', fontSize: '13px', marginTop: '12px' }}>
            {answeredCount} av {totalCount} har svarat
          </div>

        </Col>
      </Row>
    </Container>
  );
}
